/**
 * Episode Session API Client
 *
 * API client for creating and managing episode WebRTC sessions.
 * Used by the Episode Try page to establish conversations.
 *
 * Story 10.10: Episode Try Page with WebRTC Integration
 * Story 3.9: Session Persistence & Replay - Two-Phase Lifecycle
 */

import { apiGet, apiPost } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// Types
// ============================================================================

/**
 * Request to create a new episode session
 * Story 3.9: Two-phase lifecycle (create → connect)
 * Story 3.9.15: language removed - episodes are now language-specific
 */
export interface CreateSessionRequest {
  episode_id: string;
}

/**
 * Response from creating an episode session
 * Story 3.9: Session created but NOT connected yet
 * Story 3.9.15: language renamed to language_code
 */
/**
 * Goal progress entry from backend goals_progress array
 * Each entry represents a completed goal with its ID, text, and timestamp
 */
export interface GoalProgressEntry {
  goal_id: string;
  goal_text: string;
  completed_at: string;
}

export interface CreateSessionResponse {
  session_id: string;
  episode_id: string;
  user_id: string;
  language_code: string; // Story 3.9.15: Renamed from language
  status: 'active' | 'completed' | 'finished';
  created_at: string;
  conversation_context?: ConversationTurn[] | null;
  goals_progress?: GoalProgressEntry[] | null;
}

/**
 * Conversation turn from backend conversation_context (flat OpenAI-compatible format)
 * Story 3.9.5: Simplified Conversation Context Format Migration
 */
export interface ConversationTurn {
  role: 'user' | 'assistant' | 'tool';
  timestamp: string;
  turn_number: number;
  content?: string | null;
  // User-specific fields
  audio_file?: string;
  // Assistant-specific fields
  personas?: Array<{
    persona: string;
    message: string;
    audio_file?: string;
  }>;
  tool_calls?: Array<{
    id: string;
    type: string;
    function: {
      name: string;
      arguments: string;
    };
  }>;
  // Tool-specific fields
  tool_call_id?: string;
}

/**
 * Response from connecting to an episode session
 * Story 3.9: Establishes LiveKit connection (can be called multiple times)
 * Story 3.9.2: Added episode_id for frontend navigation and conversation_context for resume
 * KLA-261: Replaced ice_servers with LiveKit connection details
 */
export interface ConnectSessionResponse {
  session_id: string;
  episode_id: string;
  connect_url: string;
  status: 'active' | 'completed' | 'finished';
  conversation_context?: ConversationTurn[] | null;
}

/**
 * Response from media endpoint (POST to connect_url).
 * Contains LiveKit credentials for connecting to the room.
 */
export interface StartBotResponse {
  livekit_url: string;
  livekit_token: string;
  livekit_room: string;
}

/**
 * Single session item in admin sessions list
 * Story 3.9: Admin Sessions List
 * Story 3.9.15: language renamed to language_code
 */
export interface AdminSessionItem {
  session_id: string;
  episode_id: string;
  user_id: string;
  language_code: string; // Story 3.9.15: Renamed from language
  status: 'active' | 'completed' | 'finished';
  created_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  goals_completed: number;
  total_goals: number;
}

/**
 * Response from admin sessions list endpoint
 * Story 3.9: Admin Sessions List
 */
export interface AdminSessionsListResponse {
  sessions: AdminSessionItem[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new episode session (Step 1 of two-phase lifecycle)
 * Story 3.9: Session Persistence & Replay
 * Story 3.9.15: language parameter removed - episodes are language-specific
 *
 * Creates a session record in the database but does NOT establish WebRTC connection.
 * Call connectSession() afterwards to start the conversation.
 *
 * @param episodeId - UUID of the episode to test
 * @returns Session details including session_id for later connection
 * @throws Error if active session already exists (use connectSession() to resume)
 */
export async function createSession(episodeId: string): Promise<CreateSessionResponse> {
  try {
    const payload: CreateSessionRequest = {
      episode_id: episodeId,
    };

    const response = await apiPost<CreateSessionResponse>(`/api/episodes/sessions/create`, payload);

    logger.info('Episode session created successfully', {
      sessionId: response.session_id,
      episodeId,
    });

    return response;
  } catch (error) {
    logger.error('Failed to create episode session', { episodeId, error });
    throw error;
  }
}

/**
 * Get episode session metadata without establishing WebRTC connection
 * Story 3.9.2: Session-centric architecture
 *
 * Returns basic session info (episode_id, language, status) needed to display
 * the Try page before establishing WebRTC connection.
 *
 * @param sessionId - UUID of the session
 * @returns Session metadata
 * @throws Error if session not found or user not authorized
 */
export async function getSessionMetadata(sessionId: string): Promise<CreateSessionResponse> {
  try {
    const response = await apiGet<CreateSessionResponse>(`/api/episodes/sessions/${sessionId}`);

    logger.info('Episode session metadata retrieved', {
      sessionId: response.session_id,
      episodeId: response.episode_id,
    });

    return response;
  } catch (error) {
    logger.error('Failed to get session metadata', { sessionId, error });
    throw error;
  }
}

/**
 * Connect to an existing episode session (Step 2 of two-phase lifecycle)
 * Story 3.9: Session Persistence & Replay
 *
 * Establishes WebRTC connection for an existing session.
 * Can be called multiple times to resume after disconnection.
 *
 * @param sessionId - UUID of the session to connect to
 * @returns WebRTC connection details including URL and ICE servers
 * @throws Error if session doesn't exist or is finished
 */
export async function connectSession(sessionId: string): Promise<ConnectSessionResponse> {
  try {
    const response = await apiPost<ConnectSessionResponse>(
      `/api/episodes/sessions/${sessionId}/connect`,
      {} // Empty body - session_id in URL
    );

    logger.info('Episode session connected successfully', {
      sessionId: response.session_id,
      webrtcUrl: response.connect_url,
    });

    return response;
  } catch (error) {
    logger.error('Failed to connect episode session', { sessionId, error });
    throw error;
  }
}

/**
 * Fetch admin sessions list for an episode
 * Story 3.9: Admin Sessions View UI
 *
 * Returns list of sessions for the specified episode filtered by current admin user.
 * Sessions are sorted by created_at DESC (most recent first).
 *
 * @param episodeId - UUID of the episode
 * @param limit - Number of items per page (default: 10)
 * @param offset - Pagination offset (default: 0)
 * @returns List of sessions with pagination metadata
 * @throws Error if episode not found or user not authorized
 */
export async function getEpisodeSessions(
  episodeId: string,
  limit: number = 10,
  offset: number = 0
): Promise<AdminSessionsListResponse> {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const response = await apiGet<AdminSessionsListResponse>(
      `/api/admin/episodes/${episodeId}/sessions?${params.toString()}`
    );

    logger.info('Episode sessions fetched successfully', {
      episodeId,
      total: response.total,
      limit,
      offset,
    });

    return response;
  } catch (error) {
    logger.error('Failed to fetch episode sessions', { episodeId, error });
    throw error;
  }
}

/**
 * Finish an episode session
 * Story 3.9: Admin Session Management
 *
 * Marks a session as finished with current timestamp. Used by admins to
 * manually end sessions that are stuck or need to be terminated.
 * Uses the same user endpoint since admins are users.
 *
 * @param sessionId - UUID of the session to finish
 * @throws Error if session not found or user not authorized
 */
export async function finishSession(sessionId: string): Promise<void> {
  try {
    await apiPost<void>(`/api/episodes/sessions/${sessionId}/finish`, {});

    logger.info('Episode session finished successfully', { sessionId });
  } catch (error) {
    logger.error('Failed to finish episode session', { sessionId, error });
    throw error;
  }
}
